import './assets/styles.css';

import { Command, Plugin } from '@ckeditor/ckeditor5-core';
import { ButtonView } from '@ckeditor/ckeditor5-ui';
import { Widget, toWidget, toWidgetEditable } from '@ckeditor/ckeditor5-widget';

export default class TwoColumsBox extends Plugin {
    static get requires() {
        return [ TwoColumnsBoxEditing, TwoColumnsBoxUi ];
    }
}

class TwoColumnsBoxUi extends Plugin {
    init() {
        const editor = this.editor;
        const t = editor.t;

        editor.ui.componentFactory.add( 'twoColumnsBox', locale => {
            // The state of the button will be bound to the widget command.
            const command = editor.commands.get( 'insertTwoColumnsBox' );

            // The button will be an instance of ButtonView.
            const buttonView = new ButtonView( locale );

            buttonView.set( {
                // The t() function helps localize the editor. All strings enclosed in t() can be
                // translated and change when the language of the editor changes.
                label: t( '2☐' ),
                withText: true,
                tooltip: true
            } );

            // Bind the state of the button to the command.
            buttonView.bind( 'isOn', 'isEnabled' ).to( command, 'value', 'isEnabled' );

            // Execute the command when the button is clicked (executed).
            this.listenTo( buttonView, 'execute', () => editor.execute( 'insertTwoColumnsBox' ) );

            return buttonView;
        } );
    }
}

class TwoColumnsBoxEditing extends Plugin {
    static get requires() {
        return [ Widget ];
    }

    init() {

        this._defineSchema();
        this._defineConverters();

        this.editor.commands.add( 'insertTwoColumnsBox', new InsertTwoColumnsBoxCommand( this.editor ) );
    }

    _defineSchema() {
        const schema = this.editor.model.schema;

        schema.register( 'twoColumnsBox', {
      
            inheritAllFrom: '$blockObject'
        } );

        schema.register( 'twoColumnsBoxColLeft', {
        
            isLimit: true,

            allowIn: 'twoColumnsBox',

         
            allowContentOf: '$root'
        } );

        schema.register( 'twoColumnsBoxColRight', {
          
            isLimit: true,

            allowIn: 'twoColumnsBox',

  
            allowContentOf: '$root'
        } );

        schema.addChildCheck( ( context, childDefinition ) => {
            if ( context.endsWith( 'twoColumnsBoxColRight' ) && childDefinition.name == 'twoColumnsBox' ) {
                return false;
            }
        } );
    }

    _defineConverters() {
        const conversion = this.editor.conversion;

        // <simpleBox> converters
        conversion.for( 'upcast' ).elementToElement( {
            model: 'twoColumnsBox',
            view: {
                name: 'section',
                classes: 'two-columns-box'
            }
        } );
        conversion.for( 'dataDowncast' ).elementToElement( {
            model: 'twoColumnsBox',
            view: {
                name: 'section',
                classes: 'two-columns-box'
            }
        } );
        conversion.for( 'editingDowncast' ).elementToElement( {
            model: 'twoColumnsBox',
            view: ( modelElement, { writer: viewWriter } ) => {
                const section = viewWriter.createContainerElement( 'section', { class: 'two-columns-box' } );

                return toWidget( section, viewWriter, { label: 'simple box widget' } );
            }
        } );

        // <simpleBoxTitle> converters
        conversion.for( 'upcast' ).elementToElement( {
            model: 'twoColumnsBoxColLeft',
            view: {
                name: 'div',
                classes: 'two-columns-box__col'
            }
        } );
        conversion.for( 'dataDowncast' ).elementToElement( {
            model: 'twoColumnsBoxColLeft',
            view: {
                name: 'div',
                classes: 'two-columns-box__col'
            }
        } );
        conversion.for( 'editingDowncast' ).elementToElement( {
            model: 'twoColumnsBoxColLeft',
            view: ( modelElement, { writer: viewWriter } ) => {
               
                const h1 = viewWriter.createEditableElement( 'div', { class: 'two-columns-box__col' } );

                return toWidgetEditable( h1, viewWriter );
            }
        } );

        // <simpleBoxDescription> converters
        conversion.for( 'upcast' ).elementToElement( {
            model: 'twoColumnsBoxColRight',
            view: {
                name: 'div',
                classes: 'two-columns-box__col'
            }
        } );
        conversion.for( 'dataDowncast' ).elementToElement( {
            model: 'twoColumnsBoxColRight',
            view: {
                name: 'div',
                classes: 'two-columns-box__col'
            }
        } );
        conversion.for( 'editingDowncast' ).elementToElement( {
            model: 'twoColumnsBoxColRight',
            view: ( modelElement, { writer: viewWriter } ) => {
            
                const div = viewWriter.createEditableElement( 'div', { class: 'two-columns-box__col' } );

                return toWidgetEditable( div, viewWriter );
            }
        } );
    }
}

class InsertTwoColumnsBoxCommand extends Command {
    execute() {
        this.editor.model.change( writer => {
            this.editor.model.insertObject( createTwoColumnsBox( writer ) );
        } );
    }

    refresh() {
        const model = this.editor.model;
        const selection = model.document.selection;
        const allowedIn = model.schema.findAllowedParent( selection.getFirstPosition(), 'twoColumnsBox' );

        this.isEnabled = allowedIn !== null;
    }
}

function createTwoColumnsBox( writer ) {
    const twoColumnsBox = writer.createElement( 'twoColumnsBox' );
    const twoColumnsBoxColLeft = writer.createElement( 'twoColumnsBoxColLeft' );
    const twoColumnsBoxColRight = writer.createElement( 'twoColumnsBoxColRight' );

    writer.append( twoColumnsBoxColLeft, twoColumnsBox );
    writer.append( twoColumnsBoxColRight, twoColumnsBox );

    // There must be at least one paragraph for the description to be editable.
    // See https://github.com/ckeditor/ckeditor5/issues/1464.
    writer.appendElement( 'paragraph', twoColumnsBoxColLeft );
    writer.appendElement( 'paragraph', twoColumnsBoxColRight );

    return twoColumnsBox;
}
